import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { addDays, formatRange, rupiah, waLink } from "@/lib/format";
import { packageLimits, packageTotal } from "@/lib/pricing";
import { useSettings } from "@/hooks/queries";

const Field = ({ label, children }) => (
  <label className="block space-y-2">
    <span className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</span>
    {children}
  </label>
);

const Stepper = ({ value, low, high, onChange }) => {
  const btn = "flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 transition-colors hover:border-gold/60 disabled:opacity-30";
  return (
    <div className="flex items-center gap-2">
      <button type="button" className={btn} disabled={value <= low} onClick={() => onChange(value - 1)} data-testid="booking-participants-minus"><Minus className="h-4 w-4" /></button>
      <input type="number" min={low} max={high} value={value} onChange={(e) => onChange(Math.max(low, Math.min(high, Number(e.target.value) || low)))} className="field w-20 text-center font-mono" data-testid="booking-input-participants" />
      <button type="button" className={btn} disabled={value >= high} onClick={() => onChange(value + 1)} data-testid="booking-participants-plus"><Plus className="h-4 w-4" /></button>
      <span className="text-xs text-neutral-500">{low}–{high} orang</span>
    </div>
  );
};

export const BookingForm = ({ trip, pkg, date }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: settings } = useSettings();
  const [low, high] = packageLimits(pkg);
  const [pax, setPax] = useState(low);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", notes: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const total = packageTotal(pkg, pax);

  useEffect(() => {
    setPax((p) => Math.max(low, Math.min(high, p)));
  }, [low, high]);

  const mutation = useMutation({
    mutationFn: (payload) => api.post("/orders", payload).then((r) => r.data),
    onSuccess: (order) => {
      toast.success("Pesanan berhasil dibuat");
      qc.invalidateQueries({ queryKey: ["occupancy"] });
      navigate(`/pesanan/${order.code}`);
    },
    onError: (err) => {
      toast.error(formatApiError(err));
      qc.invalidateQueries({ queryKey: ["occupancy"] });
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!date) return toast.error("Pilih tanggal keberangkatan terlebih dahulu");
    mutation.mutate({ ...form, participants: pax, trip_id: trip.id, package_id: pkg.id, start_date: date });
  };

  return (
    <form onSubmit={submit} className="panel space-y-5 p-5 sm:p-7" data-testid="booking-form">
      <div>
        <p className="overline-label">Langkah 3</p>
        <h3 className="mt-2 font-display text-2xl">Data pemesan</h3>
      </div>
      <Field label="Nama lengkap">
        <input required minLength={2} value={form.customer_name} onChange={set("customer_name")} className="field" placeholder="Nama sesuai KTP" data-testid="booking-input-name" />
      </Field>
      <Field label="No. WhatsApp">
        <input required type="tel" minLength={8} value={form.phone} onChange={set("phone")} className="field" placeholder="08xxxxxxxxxx" data-testid="booking-input-phone" />
      </Field>
      <Field label="Email (opsional)">
        <input type="email" value={form.email} onChange={set("email")} className="field" placeholder="nama@email.com" data-testid="booking-input-email" />
      </Field>
      <Field label="Jumlah peserta">
        <Stepper value={pax} low={low} high={high} onChange={setPax} />
      </Field>
      {settings?.whatsapp && (
        <a href={waLink(settings.whatsapp, `Halo, saya ingin private trip ${trip.name} (${pkg.name}) untuk lebih dari ${high} orang.`)} target="_blank" rel="noreferrer" className="block text-xs text-neutral-500 underline-offset-4 hover:text-gold hover:underline" data-testid="booking-big-group-link">
          Rombongan lebih dari {high} orang? Hubungi admin
        </a>
      )}
      <Field label="Catatan (opsional)">
        <textarea rows={3} value={form.notes} onChange={set("notes")} className="field h-auto py-3" placeholder="Titik jemput, kebutuhan khusus, dll." data-testid="booking-input-notes" />
      </Field>
      <div className="space-y-2 rounded-xl border border-white/10 bg-black/40 p-4 text-sm">
        <div className="flex justify-between gap-4"><span className="text-neutral-500">Paket</span><span className="text-right">{pkg.name}</span></div>
        <div className="flex justify-between gap-4"><span className="text-neutral-500">Tanggal</span><span className="text-right">{date ? formatRange(date, addDays(date, pkg.duration_days - 1)) : "-"}</span></div>
        <div className="flex justify-between gap-4"><span className="text-neutral-500">Peserta</span><span>{pax} orang</span></div>
        <div className="flex items-baseline justify-between gap-4 border-t border-white/10 pt-3">
          <span className="text-neutral-400">Total</span>
          <span className="font-mono text-xl text-gold-light" data-testid="booking-total-price">{total == null ? "-" : rupiah(total)}</span>
        </div>
      </div>
      <button type="submit" className="btn-gold w-full" disabled={mutation.isPending || total == null} data-testid="booking-btn-submit">
        {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Kirim Pesanan
      </button>
      <p className="text-center text-xs text-neutral-500">Pembayaran via transfer manual. Instruksi muncul setelah pesanan dikirim.</p>
    </form>
  );
};
